import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Loader2, Lock, Search, Users, X } from "lucide-react";
import axios from "axios";
import Monogram from "./Monogram";
import { getLenis } from "@/lib/lenis";
import { WEDDING } from "@/data/wedding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mariage_admin_token";

const formatCompanions = (companions, sep = ", ") =>
  (companions || [])
    .map((c) => {
      const name = `${c.firstname} ${c.lastname}`.trim();
      return c.restrictions?.length ? `${name} (${c.restrictions.join(", ")})` : name;
    })
    .join(sep);

const StatCard = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-line bg-cream p-5"
  >
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{label}</p>
    <p className="mt-2 font-serif text-4xl text-ink">{value}</p>
  </div>
);

export default function AdminGate({ open, onClose }) {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorCount, setErrorCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const [guests, setGuests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const lenis = getLenis();
    if (open) {
      lenis?.stop();
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (t) {
        setAuthed(true);
        fetchGuests(t);
      }
    } else {
      lenis?.start();
      setPassword("");
      setError("");
      setQuery("");
    }
    return undefined;
  }, [open]);

  const fetchGuests = async (token) => {
    setLoadingGuests(true);
    try {
      const { data } = await axios.get(`${API}/admin/guests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuests(data.guests || []);
      const logsRes = await axios.get(`${API}/admin/login-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setError("Session expirée, merci de saisir à nouveau le mot de passe.");
        setErrorCount((c) => c + 1);
      }
    } finally {
      setLoadingGuests(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setAuthed(true);
      fetchGuests(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Mot de passe incorrect");
      setErrorCount((c) => c + 1);
    } finally {
      setChecking(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setGuests([]);
    setLogs([]);
    setPassword("");
  };

  const stats = useMemo(() => {
    const present = guests.filter((g) => g.attending === "oui");
    const absent = guests.filter((g) => g.attending === "non");
    const companionsCount = present.reduce((s, g) => s + (g.companions?.length || 0), 0);
    const menus = {};
    guests.forEach((g) => {
      if (g.attending !== "oui") return;
      (g.restrictions || []).forEach((r) => {
        menus[r] = (menus[r] || 0) + 1;
      });
      (g.companions || []).forEach((c) => {
        (c.restrictions || []).forEach((r) => {
          menus[r] = (menus[r] || 0) + 1;
        });
      });
    });
    const allergies = guests.filter((g) =>
      (g.restrictions || []).some((r) => r.toLowerCase().includes("allerg"))
    ).length;
    return {
      total: guests.length,
      presents: present.length + companionsCount,
      absents: absent.length,
      taux: guests.length ? Math.round((present.length / guests.length) * 100) : 0,
      menus,
      allergies,
    };
  }, [guests]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter((g) => {
      const hay = `${g.firstname} ${g.lastname} ${(g.companions || [])
        .map((c) => `${c.firstname} ${c.lastname}`)
        .join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [guests, query]);

  const exportCsv = () => {
    const rows = [
      ["Nom", "Prénom", "Email", "Présence", "Accompagnants", "Restrictions", "Allergies", "Message", "Date"],
    ];
    guests.forEach((g) => {
      rows.push([
        g.lastname,
        g.firstname,
        g.email,
        g.attending === "oui" ? "Présent(e)" : "Absent(e)",
        formatCompanions(g.companions, " ; "),
        (g.restrictions || []).join(" ; "),
        g.allergies,
        g.message,
        new Date(g.created_at).toLocaleString("fr-FR"),
      ]);
    });
    const csv =
      "\uFEFF" +
      rows
        .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
        .join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "invites_mariage_morgane_bruno.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="admin-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 48 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="admin-modal"
            className="relative flex h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-3xl border border-line bg-sand shadow-2xl sm:h-[85dvh] sm:rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4 sm:px-7">
              <div className="flex items-center gap-3">
                <Monogram className="h-9 w-9" />
                <div>
                  <p className="font-serif text-xl leading-none text-ink">Espace Mariés</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                    {WEDDING.bride} &amp; {WEDDING.groom} — {WEDDING.dateShort}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {authed && (
                  <>
                    <button
                      type="button"
                      data-testid="admin-export-csv-button"
                      onClick={exportCsv}
                      className="hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-sand transition hover:bg-terracotta sm:inline-flex"
                    >
                      <Download className="h-4 w-4" />
                      Exporter CSV
                    </button>
                    <button
                      type="button"
                      data-testid="admin-logout-button"
                      onClick={logout}
                      className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-sm text-taupe transition hover:border-terracotta hover:text-terracotta"
                    >
                      <Users className="hidden h-4 w-4" />
                      Déconnexion
                    </button>
                  </>
                )}
                <button
                  type="button"
                  data-testid="admin-close-button"
                  onClick={onClose}
                  aria-label="Fermer"
                  className="rounded-full p-2.5 text-fog transition hover:bg-cream hover:text-ink"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {!authed ? (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-6 py-12" data-lenis-prevent>
                <motion.form
                  onSubmit={login}
                  key={errorCount}
                  initial={errorCount ? { x: 0 } : false}
                  animate={error ? { x: [0, -8, 8, -4, 4, 0] } : { x: 0 }}
                  transition={{ duration: 0.4 }}
                  className="w-full max-w-sm text-center"
                  data-testid="admin-login-form"
                >
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cream text-terracotta">
                    <Lock className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-ink">Espace réservé</h3>
                  <p className="mt-3 text-sm leading-relaxed text-taupe">
                    Cet espace est réservé aux mariés. Merci de saisir le mot de passe
                    pour consulter la liste des invités.
                  </p>
                  <input
                    type="password"
                    data-testid="admin-password-input"
                    placeholder="Mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-7 w-full rounded-xl border border-line bg-paper px-4 py-3.5 text-center text-ink outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
                  />
                  {error && (
                    <p data-testid="admin-login-error" className="mt-3 text-sm text-[#B4574A]">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="admin-login-button"
                    disabled={checking}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-sand transition hover:bg-terracotta disabled:opacity-60"
                  >
                    {checking ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Vérification…
                      </>
                    ) : (
                      "Accéder à l'espace mariés"
                    )}
                  </button>
                </motion.form>
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 py-6 sm:px-7" data-lenis-prevent>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <StatCard label="Réponses reçues" value={stats.total} testid="admin-stat-total" />
                  <StatCard label="Présents (avec accompagnants)" value={stats.presents} testid="admin-stat-presents" />
                  <StatCard label="Absents" value={stats.absents} testid="admin-stat-absents" />
                  <StatCard label="Taux de présence" value={`${stats.taux}%`} testid="admin-stat-taux" />
                </div>

                <div className="flex flex-wrap items-center gap-2" data-testid="admin-menu-chips">
                  <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                    Décompte des menus :
                  </span>
                  {Object.entries(stats.menus).map(([label, count]) => (
                    <span
                      key={label}
                      className="rounded-full border border-line bg-cream px-4 py-1.5 text-xs text-ink"
                    >
                      {label} <strong className="font-semibold text-terracotta-deep">×{count}</strong>
                    </span>
                  ))}
                  {stats.allergies > 0 && (
                    <span className="rounded-full border border-[#B4574A]/30 bg-[#B4574A]/5 px-4 py-1.5 text-xs text-[#B4574A]">
                      Allergies déclarées ×{stats.allergies}
                    </span>
                  )}
                </div>

                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" />
                  <input
                    data-testid="admin-search-input"
                    placeholder="Rechercher un invité…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full rounded-xl border border-line bg-paper py-3 pl-11 pr-4 text-sm text-ink outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
                  />
                </div>

                <div className="space-y-3 sm:hidden" data-testid="admin-guest-cards">
                  {filtered.map((g) => (
                    <div key={g.id} className="rounded-2xl border border-line bg-paper p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink">
                            {g.lastname.toUpperCase()} {g.firstname}
                          </p>
                          {g.email && (
                            <p className="break-all text-xs text-fog">{g.email}</p>
                          )}
                        </div>
                        <span
                          className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs ${
                            g.attending === "oui"
                              ? "bg-sage/15 text-sage-deep"
                              : "bg-[#B4574A]/10 text-[#B4574A]"
                          }`}
                        >
                          {g.attending === "oui" ? "Présent(e)" : "Absent(e)"}
                        </span>
                      </div>
                      <dl className="mt-3 space-y-1.5 text-sm">
                        <div className="flex justify-between gap-3">
                          <dt className="text-fog">Accompagnants</dt>
                          <dd className="text-right text-taupe">
                            {g.companions?.length ? formatCompanions(g.companions) : "—"}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-fog">Restrictions</dt>
                          <dd className="text-right text-taupe">
                            {g.restrictions?.length
                              ? `${g.restrictions.join(", ")}${g.allergies ? ` (${g.allergies})` : ""}`
                              : "—"}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-fog">Reçu le</dt>
                          <dd className="font-mono text-xs text-fog">
                            {new Date(g.created_at).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                  {!filtered.length && (
                    <p className="rounded-2xl border border-line bg-paper px-4 py-10 text-center text-sm text-taupe">
                      {loadingGuests
                        ? "Chargement des réponses…"
                        : "Aucune réponse pour le moment — les invitations font leur chemin."}
                    </p>
                  )}
                </div>

                <div className="hidden overflow-x-auto rounded-2xl border border-line sm:block">
                  <table data-testid="admin-guests-table" className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="bg-cream text-left font-mono text-[10px] uppercase tracking-[0.15em] text-fog">
                        <th className="px-4 py-3.5 font-medium">Invité</th>
                        <th className="px-4 py-3.5 font-medium">Présence</th>
                        <th className="px-4 py-3.5 font-medium">Accompagnants</th>
                        <th className="px-4 py-3.5 font-medium">Restrictions</th>
                        <th className="px-4 py-3.5 font-medium">Reçu le</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((g) => (
                        <tr key={g.id} className="border-t border-line bg-paper">
                          <td className="px-4 py-3.5">
                            <span className="font-medium text-ink">
                              {g.lastname.toUpperCase()} {g.firstname}
                            </span>
                            {g.email && (
                              <span className="block text-xs text-fog">{g.email}</span>
                            )}
                            {g.message && (
                              <span className="block max-w-[240px] truncate text-xs italic text-fog">
                                « {g.message} »
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <span
                              data-testid={`admin-badge-${g.attending}`}
                              className={`inline-flex rounded-full px-3 py-1 text-xs ${
                                g.attending === "oui"
                                  ? "bg-sage/15 text-sage-deep"
                                  : "bg-[#B4574A]/10 text-[#B4574A]"
                              }`}
                            >
                              {g.attending === "oui" ? "Présent(e)" : "Absent(e)"}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-taupe">
                            {g.companions?.length ? formatCompanions(g.companions) : "—"}
                          </td>
                          <td className="px-4 py-3.5 text-taupe">
                            {g.restrictions?.length
                              ? `${g.restrictions.join(", ")}${g.allergies ? ` (${g.allergies})` : ""}`
                              : "—"}
                          </td>
                          <td className="px-4 py-3.5 font-mono text-xs text-fog">
                            {new Date(g.created_at).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))}
                      {!filtered.length && (
                        <tr className="border-t border-line bg-paper">
                          <td colSpan={5} className="px-4 py-12 text-center text-taupe">
                            {loadingGuests
                              ? "Chargement des réponses…"
                              : "Aucune réponse pour le moment — les invitations font leur chemin."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="overflow-hidden rounded-2xl border border-line">
                  <p
                    className="bg-cream px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-fog"
                    data-testid="admin-logs-title"
                  >
                    Journal des connexions
                  </p>
                  <div className="max-h-56 overflow-y-auto" data-lenis-prevent data-testid="admin-login-logs">
                    {logs.map((l) => (
                      <div
                        key={l.id}
                        className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-paper px-4 py-2.5 text-sm"
                      >
                        <span className="font-mono text-xs text-fog">
                          {new Date(l.created_at).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <span className="text-xs text-taupe">{l.ip}</span>
                        <span
                          className={`inline-flex rounded-full px-3 py-0.5 text-xs ${
                            l.status === "succès"
                              ? "bg-sage/15 text-sage-deep"
                              : l.status === "bloqué"
                                ? "bg-gold/15 text-[#8a6d2b]"
                                : "bg-[#B4574A]/10 text-[#B4574A]"
                          }`}
                        >
                          {l.status === "succès"
                            ? "Connexion réussie"
                            : l.status === "bloqué"
                              ? "Tentative bloquée"
                              : "Mot de passe incorrect"}
                        </span>
                      </div>
                    ))}
                    {!logs.length && (
                      <p className="border-t border-line bg-paper px-4 py-6 text-center text-sm text-taupe">
                        Aucune connexion enregistrée pour le moment.
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  data-testid="admin-export-csv-button-mobile"
                  onClick={exportCsv}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-medium text-sand transition hover:bg-terracotta sm:hidden"
                >
                  <Download className="h-4 w-4" />
                  Exporter en CSV
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
